/**
 * NameSoftAsk — мягкое одноразовое приглашение представиться.
 *
 * JB task #56 / doc 18 §progressive profile. Открывает name_signature
 * артефакт (trigger: profileNameSet) + создаёт первое личное вовлечение
 * («Сай знает моё имя»).
 *
 * Условия показа:
 *   - игрок сделал хотя бы 8 тапов (не на старте — чтобы не пугать
 *     модалом раньше первой активации)
 *   - displayName ещё не установлено
 *   - nameAsked ещё false (после показа = true, повторно не появляется)
 *   - не блокирован активацией Сай / reveal'ом артефакта
 *
 * Эмоция: «только если хочешь». Кнопка «пропустить» равноправна с
 * «сохранить». Без давления, без негативных частиц.
 */
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useArtifactsStore } from '../visual-artifacts/store.js';
import { useGameStore } from '../stores/game.js';
import { useProfileAskStore } from '../stores/profileAsk.js';
import { generateText } from '../telemetry/aiProxy.js';
import { emit } from '../telemetry/emit.js';
import styles from './NameSoftAsk.module.css';

/** Минимум тапов перед показом — пусть игрок «прогреется». */
const MIN_TAPS_FOR_ASK = 8;

export function NameSoftAsk(): JSX.Element {
  const totalTaps = useGameStore((s) => s.state.stats.totalTaps);
  const displayName = useGameStore((s) => s.state.profile.displayName);
  const setDisplayName = useGameStore((s) => s.setDisplayName);
  const activeActivation = useGameStore((s) => s.activeActivation);
  const revealQueue = useArtifactsStore((s) => s.revealQueue);

  const nameAsked = useProfileAskStore((s) => s.nameAsked);
  const markNameAsked = useProfileAskStore((s) => s.markNameAsked);
  const epoch = useGameStore((s) => s.state.epoch);

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  // AI-generated personalized welcome — null до тех пор пока Sai ещё
  // «не узнала» player'а. После save показываем 3.5s как мягкое echo.
  const [personalWelcome, setPersonalWelcome] = useState<string | null>(null);

  // 2026-05-26 BUG FIX: track mounted state — generateText().then() мог
  // вызвать setState на unmounted component если игрок быстро уходит
  // (React warning + потенциальный memory leak от dangling timeout).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Эффект-trigger: один раз поднять флажок когда условия впервые выполнились.
  useEffect(() => {
    if (nameAsked) return;
    if (displayName !== null && displayName.trim() !== '') return;
    if (totalTaps < MIN_TAPS_FOR_ASK) return;
    if (activeActivation !== null) return;
    if (revealQueue.length > 0) return;
    // Дать UI секунду «успокоиться» — не показываем сразу после тапа.
    const tid = window.setTimeout(() => {
      setOpen(true);
      emit('soft_ask.shown', {});
    }, 1200);
    return () => {
      window.clearTimeout(tid);
    };
  }, [nameAsked, displayName, totalTaps, activeActivation, revealQueue]);

  function handleSave(): void {
    const trimmed = inputValue.trim();
    const hasName = trimmed.length > 0;
    if (hasName) {
      setDisplayName(trimmed.slice(0, 32));
    }
    markNameAsked();
    // has_dob = false пока что — ДР запрашиваем позже (doc 18 progressive
    // questionnaire), отдельный модуль.
    emit('soft_ask.completed', { has_name: hasName, has_dob: false });
    // AI personalized welcome (Этап 6 backend) — fetch async, show через
    // ~1s после input close. Если backend не configured / network down /
    // rate limit — silent skip, никаких errors игроку.
    if (hasName) {
      void generateText('welcome', { name: trimmed.slice(0, 32), epoch }).then((text) => {
        // Guard против setState на unmounted (закрыл modal до того как
        // AI ответил) — без него React выбросит warning + timeout
        // продолжит висеть.
        if (!mountedRef.current) return;
        if (text === null) {
          setOpen(false);
          return;
        }
        setPersonalWelcome(text);
        // Auto-dismiss через 4.5s, потом возвращаемся в игру.
        window.setTimeout(() => {
          if (!mountedRef.current) return;
          setPersonalWelcome(null);
          setOpen(false);
        }, 4500);
      });
    } else {
      setOpen(false);
    }
  }

  function handleSkip(): void {
    markNameAsked();
    setOpen(false);
    emit('soft_ask.skipped', {});
  }

  return (
    <AnimatePresence>
      {open && (
        <div className={styles.backdrop} role="dialog" aria-modal="true">
          {/* 2026-05-26 polish: первый личный момент с Сай — мягкий
              spring чтобы card «расцветала», не выскакивала. stiffness
              260, damping 22 — почти без overshoot, тёплое появление.
              Opacity отдельно для smooth fade. */}
          <motion.div
            className={styles.card}
            initial={{ opacity: 0, y: 16, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            transition={{
              opacity: { duration: 0.4, ease: 'easeOut' },
              y: { type: 'spring', stiffness: 260, damping: 22 },
              scale: { type: 'spring', stiffness: 260, damping: 22 },
            }}
          >
            {personalWelcome !== null ? (
              <>
                <p className={styles.preface}>Сай узнала тебя</p>
                <p className={styles.body}>{personalWelcome}</p>
                <p className={styles.hint}>имя останется только у тебя.</p>
              </>
            ) : (
              <>
                <p className={styles.preface}>Сай хочет узнать тебя</p>
                <p className={styles.body}>
                  как тебя зовут?
                  <br />
                  только если хочешь — поделись именем.
                </p>
                <input
                  type="text"
                  className={styles.input}
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                  }}
                  placeholder="твоё имя"
                  autoComplete="given-name"
                  maxLength={32}
                  aria-label="Имя"
                />
                <div className={styles.actions}>
                  <button type="button" className={styles.skip} onClick={handleSkip}>
                    пропустить
                  </button>
                  <button
                    type="button"
                    className={styles.save}
                    onClick={handleSave}
                    disabled={inputValue.trim().length === 0}
                  >
                    сохранить
                  </button>
                </div>
                <p className={styles.hint}>имя останется только у тебя — без отправки кому-либо.</p>
              </>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
